import * as React from 'react';

import { Cell } from '../../components/NewbuildingsComparePage/parts/Cell';
import { DeveloperLinkCell } from '../../components/NewbuildingsComparePage/parts/Cell/cells/DeveloperCell';
import { RoomStatsToggleButton } from '../../components/NewbuildingsComparePage/parts/RoomStatsToggleButton';
import { INewbuildingsCompareDataRow, TNewbuildingsCompareViewRow } from '../../components/NewbuildingsComparePage';
import {
  INewbuildingSummaryRow,
  INewbuildingsCompareBlock,
  TNewbuildingsCompareRow,
} from '../../types/newbuildingsCompare';
import { NewbuildingsMapContainer } from '../NewbuildingsMap';
import { blockTypeDeterminer } from './blockTypeDeterminer';
import { collectMapPins } from './collectMapPins';

const VISIBLE_ROOM_STATS = 3;

export function isNewbuildingSummaryRow(row: TNewbuildingsCompareRow): row is INewbuildingSummaryRow {
  return row.type === 'newbuildingSummary';
}

/**
 * Максимальная длина списка среди ячеек строки для типов с вертикальным списком значений.
 * Используется, чтобы у высоких строк (несколько элементов) заголовок прижимался к верху,
 * а у одноэлементных — оставался по центру.
 */
function getRowMaxListLength(row: TNewbuildingsCompareRow): number {
  if (row.type === 'infrastructure') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.infrastructures?.length ?? 0));
  }

  if (row.type === 'textList') {
    return Math.max(
      0,
      ...row.cells.map(cell => (cell.data?.items?.length ?? 0) + Number(Boolean(cell.data?.countText))),
    );
  }

  if (row.type === 'transportPointExtended' || row.type === 'transportPoint') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.transportPoints?.length ?? 0));
  }

  if (row.type === 'transportDistance') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.transportDistances?.length ?? 0));
  }

  return 0;
}

interface IBuildViewRowsParams {
  blocks: INewbuildingsCompareBlock[];
  isRoomStatsExpanded: boolean;
  onToggleRoomStats: () => void;
  /** Платформа определяет представление action-строки и пинов карты. */
  platform: 'desktop' | 'mobile';
  showDeveloperLinkRow?: boolean;
}

interface IBuildViewRowsResult {
  rows: TNewbuildingsCompareViewRow[];
  summaryRow: INewbuildingSummaryRow | undefined;
}

/**
 * Разворачивает блоки сравнения ЖК в плоский список строк для рендера таблицы.
 * Используется и десктопной, и мобильной страницами сравнения.
 */
export function buildNewbuildingsCompareViewRows({
  blocks,
  isRoomStatsExpanded,
  onToggleRoomStats,
  platform,
  showDeveloperLinkRow = false,
}: IBuildViewRowsParams): IBuildViewRowsResult {
  const summaryRow = blocks.flatMap(block => block.rows).find(isNewbuildingSummaryRow);

  const rows = blocks.flatMap<TNewbuildingsCompareViewRow>((block, blockIndex) => {
    const blockType = blockTypeDeterminer(block);
    const nonSummaryRows = block.rows.filter(row => !isNewbuildingSummaryRow(row));
    const totalRoomStats = nonSummaryRows.filter(row => row.type === 'roomStats').length;
    const hasRoomStatsToggle = blockType === 'flats' && totalRoomStats > VISIBLE_ROOM_STATS;
    const visibleRoomStatsCount = isRoomStatsExpanded ? totalRoomStats : Math.min(totalRoomStats, VISIBLE_ROOM_STATS);

    let roomStatsSeen = 0;
    const dataRows: INewbuildingsCompareDataRow[] = [];
    // Строки блока в порядке отрисовки: обычные строки-данные вперемешку со строками-действиями
    // (карта, кнопка планировок). Разделители считаются только по dataRows, поэтому список держим отдельно.
    const orderedRows: TNewbuildingsCompareViewRow[] = [];
    // Data-строки всегда попадают в оба списка: orderedRows отвечает за порядок отрисовки,
    // dataRows — за расчёт разделителей.
    const pushDataRow = (dataRow: INewbuildingsCompareDataRow) => {
      dataRows.push(dataRow);
      orderedRows.push(dataRow);
    };
    // Строка «Застройщик» и идущая сразу за ней безымянная строка-продолжение объединяются
    // в группу (общий hover). Держим ссылку на застройщика, пока не встретим его продолжение.
    let pendingDeveloperRow: INewbuildingsCompareDataRow | null = null;

    nonSummaryRows.forEach((row, rowIndex) => {
      // Строка карты — это не набор ячеек, а одна кнопка «Все ЖК на карте» в первом столбце.
      if (row.type === 'map') {
        const pins = collectMapPins(row, summaryRow, blocks);

        if (pins.length) {
          orderedRows.push({
            content: <NewbuildingsMapContainer pins={pins} platform={platform} />,
            key: `${blockIndex}-map-${rowIndex}`,
            kind: 'action',
          });
        }

        return;
      }

      if (row.type === 'roomStats') {
        const isHidden = !isRoomStatsExpanded && roomStatsSeen >= VISIBLE_ROOM_STATS;
        roomStatsSeen += 1;

        if (isHidden) {
          return;
        }
      }

      const isLastVisibleRoomStats = row.type === 'roomStats' && roomStatsSeen === visibleRoomStatsCount;

      // Признак строки акций считаем по всей строке, а не по ячейке: у части ЖК акций может не быть,
      // и поячеечная проверка развалила бы отступы внутри одной строки.
      const hasCountText = row.type === 'textList' && row.cells.some(cell => Boolean(cell.data?.countText));

      const dataRow: INewbuildingsCompareDataRow = {
        // Заголовок прижимаем к верху у строк-списков и у застройщика (на уровне аватара).
        alignTitleTop:
          row.type === 'developer' ||
          row.type === 'gallery' ||
          row.type === 'districtInfrastructure' ||
          getRowMaxListLength(row) > 1,
        cells: row.cells.map((cell, index) => (
          <Cell
            cell={cell}
            blockType={blockType}
            hasCountText={hasCountText}
            key={index}
            newbuilding={row.type === 'gallery' ? (summaryRow?.cells?.[index]?.data ?? undefined) : undefined}
            type={row.type}
            withoutBottomPadding={hasRoomStatsToggle && isLastVisibleRoomStats}
          />
        )),
        key: `${blockIndex}-${row.type}-${row.title ?? rowIndex}`,
        kind: 'data',
        title: row.title,
      };

      if (showDeveloperLinkRow && row.type === 'developer') {
        const groupId = `dev-group-${blockIndex}-${rowIndex}`;
        dataRow.groupId = groupId;
        pushDataRow(dataRow);
        pushDataRow({
          cells: row.cells.map((cell, index) => <DeveloperLinkCell cell={cell} key={index} />),
          groupId,
          key: `${blockIndex}-${row.type}-link-${rowIndex}`,
          kind: 'data',
          title: null,
        });
        pendingDeveloperRow = null;

        return;
      }

      if (pendingDeveloperRow && !row.title) {
        const groupId = `dev-group-${blockIndex}-${rowIndex}`;
        pendingDeveloperRow.groupId = groupId;
        dataRow.groupId = groupId;
      }
      pendingDeveloperRow = row.type === 'developer' ? dataRow : null;

      pushDataRow(dataRow);

      // Кнопка «Показать больше» относится только к списку планировок, поэтому её место — сразу за
      // последней видимой комнатностью, а не в конце блока: бэк присылает в том же блоке и строки,
      // не относящиеся к квартирам (например застройщика).
      if (hasRoomStatsToggle && isLastVisibleRoomStats) {
        orderedRows.push({
          content: <RoomStatsToggleButton expanded={isRoomStatsExpanded} onClick={onToggleRoomStats} />,
          key: `${blockIndex}-roomstats-toggle`,
          kind: 'action',
        });
      }
    });

    // Разделители по макету: их нет у последней строки блока и во всём блоке планировок —
    // там строки комнатностей читаются как единый список, а не как отдельные параметры.
    // Скрытые roomStats уже отфильтрованы выше, поэтому «последняя» — это последняя отрисованная.
    if (blockType === 'flats') {
      dataRows.forEach(dataRow => {
        dataRow.hideDivider = true;
      });
    } else if (dataRows.length > 0) {
      dataRows[dataRows.length - 1].hideDivider = true;
    }

    if (block.title) {
      return [
        { imagePath: block.imagePath, key: `block-${blockIndex}`, kind: 'block', title: block.title },
        ...orderedRows,
      ];
    }

    return orderedRows;
  });

  return { rows, summaryRow };
}
